// What a person is expected to work, and therefore what they owe.
//
// One rule, used by every surface that shows a target: the HR contracted-hours
// card, the org-wide monthly summary, My Space and Timesheets.
//
//   required = personDay × (org workdays − holidays − charged PAID leave)
//
// Before this, a target was `avail_hrs` (a flat 40) or `orgPerDay × workdays`,
// depending on which card you looked at — and neither knew about leave. The studio
// already does this correctly on a spreadsheet: 24 working days × 6 h/day = 144,
// where 24 is the month's calendar days minus Sundays minus three company days.
//
// Two things this module must not do, both of which are bugs the repo already paid
// for once:
//
//   * It must not decide what a working day is. `workingDayList` (lib/workdays.ts)
//     mirrors SQL `is_org_workday()`; a second opinion here is how the HR leave
//     calendar came to show "4 days off" for a Fri-Mon leave the server charged 2.
//
//   * It must not decide how much leave was charged. `reconcile_leave_clash` moves
//     the balance by `day_count − Σ worked_fraction`, so that is the number netted
//     out here. Any other formula puts the target at odds with the leave balance
//     shown on the same screen.
//
// UNPAID leave is deliberately NOT netted out: an LOP day keeps its required hours
// and surfaces as a shortfall, which is what payroll needs to see.
import { workingDayList, type Workdays } from './workdays';

/** An approved leave span, joined to its type's `paid` flag. */
export type LeaveSpan = {
  start_date: string;
  end_date: string;
  /** true ⇒ a single working day billed 0.5 (the server refuses a multi-day half). */
  half_day: boolean;
  leave_type_id: string;
  paid: boolean;
};

/** A day of approved leave that was actually worked, from `leave_day_exceptions`. */
export type WorkedException = { date: string; worked_fraction: number };

/** dateKey → the fraction of that day charged as PAID leave (1 | 0.5). */
export type ChargedLeave = ReadonlyMap<string, number>;

/**
 * Paid-leave fractions per day inside [from, to], net of days worked back.
 *
 * Spans are expanded through `workingDayList`, so a weekend or holiday inside a
 * span contributes nothing — it was never charged in the first place.
 */
export const chargedLeaveByDay = (
  spans: readonly LeaveSpan[],
  exceptions: readonly WorkedException[],
  from: string,
  to: string,
  workdays?: Workdays | null,
  holidays?: ReadonlySet<string>,
  /**
   * Which kind of span to expand. Defaults to 'paid' so every existing caller is
   * unchanged — and they must stay that way: this feeds requiredHours, and
   * netting UNPAID leave out of the requirement is exactly what would stop an
   * LOP day reaching payroll as a shortfall.
   *
   * 'unpaid' exists so the monthly table can COUNT those days without them
   * touching the requirement. Before it, the table built a column per leave type
   * but attributed days only to paid spans, so an unpaid type's column rendered
   * 0 for everyone forever — a real header with a permanently fake value.
   */
  which: 'paid' | 'unpaid' = 'paid',
): ChargedLeave => {
  const workedBack = new Map(exceptions.map((e) => [e.date, e.worked_fraction]));
  const out = new Map<string, number>();

  for (const span of spans) {
    if (span.paid !== (which === 'paid')) continue;
    // Clip to the window before expanding: a span can start in the previous month
    // or run into the next, and only the days inside [from, to] are this month's.
    const start = span.start_date > from ? span.start_date : from;
    const end = span.end_date < to ? span.end_date : to;
    const base = span.half_day ? 0.5 : 1;

    for (const day of workingDayList(start, end, workdays, holidays)) {
      const charged = Math.min(base, Math.max(0, base - (workedBack.get(day) ?? 0)));
      // Overlapping approved spans on one date take the MAX, never the sum. The
      // server picks exactly one (`order by created_at, id limit 1`), so summing
      // would net out "2 days off" on a single date — the same direction the
      // un-approve refund bug went before 20260728100000 fixed it.
      out.set(day, Math.min(1, Math.max(out.get(day) ?? 0, charged)));
    }
  }
  return out;
};

export type ExpectedDays = {
  /** Org workdays minus holidays — their spreadsheet's "Total Working Days". */
  workingDays: number;
  /** Days of paid leave charged in the window. */
  leaveDays: number;
  /** What is actually expected of the person: workingDays − leaveDays. */
  expected: number;
};

export const expectedDays = (
  from: string,
  to: string,
  workdays?: Workdays | null,
  holidays?: ReadonlySet<string>,
  charged: ChargedLeave = new Map(),
): ExpectedDays => {
  const days = workingDayList(from, to, workdays, holidays);
  let leaveDays = 0;
  for (const day of days) leaveDays += charged.get(day) ?? 0;
  return {
    workingDays: days.length,
    leaveDays,
    // Guard the floor: a leave day the workday rule excludes cannot make the
    // expectation negative, which would turn required hours into a credit.
    expected: Math.max(0, days.length - leaveDays),
  };
};

/** The hours a person owes for a window. Rounded to 0.01h so it prints cleanly. */
export const requiredHours = (perDay: number, expected: number): number =>
  Math.round(perDay * expected * 100) / 100;

/**
 * Worked ÷ required, as a percentage. UNCAPPED — 109.44% is a real and important
 * reading, and the HR card's `Math.min(100, …)` is precisely why it could never
 * appear. Clamp the progress BAR at the render site; never the number.
 *
 * Returns 0 rather than Infinity/NaN when nothing is required (a month entirely
 * of leave or holidays), so no caller has to guard a divide.
 */
export const completionPct = (workedHours: number, required: number): number =>
  required > 0 ? Math.round((workedHours / required) * 1000) / 10 : 0;

/** Seconds → `H:MM:SS`, the format their existing sheet reports hours in. */
export const fmtHms = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
